<!--
  What this audience actually has to sell, by product category.

  Counted through the audience's DESIGN GROUPS rather than a template's own industry/topic anchor, because
  that is what generation targets — so each row is the number a generation run would actually move.

  Nothing is generated from here. The Template Designer owns the design-group tree, the brand library and the
  batch pipeline, so everything actionable on this tab is a link into that audience's workspace over there.
  This screen reports; that one fills.

  Re-read every time the tab is opened, for the same reason the gap audit is: a batch landing over there
  changes the answer, and a cached inventory is a wrong inventory.
-->
<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3">
      <div class="text-caption grey--text">
        <template v-if="inventory.designGroupCount">
          {{ inventory.templateCount }} published template{{ inventory.templateCount === 1 ? '' : 's' }}
          across {{ inventory.designGroupCount }} design group{{ inventory.designGroupCount === 1 ? '' : 's' }},
          aiming for {{ inventory.target }} per group.
        </template>
        <template v-else>
          This audience has no design groups of its own yet.
        </template>
      </div>
      <v-spacer />

      <!-- The one way to act on anything on this tab: the audience's own workspace in the Template Designer,
           where its design groups live and where a batch is started. -->
      <v-btn
        v-if="designerUrl"
        small
        outlined
        color="mcsprimary"
        class="mr-2"
        :href="designerUrl"
        target="_blank"
        rel="noopener"
        title="Open this audience's design groups in the Template Designer"
      >
        <v-icon
          left
          small
        >
          mdi-open-in-new
        </v-icon>
        Open in Template Designer
      </v-btn>

      <v-btn
        small
        icon
        title="Re-read the inventory"
        :loading="loading"
        @click="load"
      >
        <v-icon small>
          mdi-refresh
        </v-icon>
      </v-btn>
    </div>

    <!-- Brands first, on purpose: a bucket without an approved brand can't be generated into at all, so it
         explains a stuck row below before the admin follows the link out and finds the bolt refused. -->
    <v-card
      v-if="brandBuckets.length"
      outlined
      class="mb-4"
    >
      <!-- Every chip here is a way into the Brand Library for the bucket it describes — the status chip for
           the audience as a whole, each bucket chip for its own topic (or the industry-level bucket). -->
      <v-card-title class="text-subtitle-1">
        Brands
        <v-chip
          small
          :color="brandChip.color"
          :outlined="brandChip.outlined"
          :dark="!brandChip.outlined"
          class="ml-2"
          :href="brandsUrl"
          :target="brandsUrl ? '_blank' : null"
          rel="noopener"
          :title="brandsUrl ? 'Open the Brand Library for this audience' : null"
        >
          {{ brandChip.label }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-2">
          {{ brandSummary }}
        </p>

        <v-chip
          v-for="bucket in brandBuckets"
          :key="bucket.topicId || 0"
          small
          outlined
          :color="bucketColor(bucket)"
          class="mr-1 mb-1"
          :title="bucketTitle(bucket)"
          :href="bucketUrl(bucket)"
          target="_blank"
          rel="noopener"
        >
          <v-icon
            v-if="!bucket.hasBrand"
            left
            x-small
          >
            mdi-alert
          </v-icon>
          {{ bucket.name }}: {{ bucket.approved }}<template v-if="brands.needsBrand">/{{ brands.minimum }}</template>
          <span
            v-if="bucket.pending"
            class="ml-1 text-caption"
          >
            (+{{ bucket.pending }} pending)
          </span>
        </v-chip>

        <div
          v-if="blockedGroupCount"
          class="text-caption warning--text mt-2"
        >
          {{ blockedGroupCount }} design group{{ blockedGroupCount === 1 ? '' : 's' }} can't be generated into
          until their bucket has an approved brand — add one in the Brand Library in the Template Designer.
        </div>
      </v-card-text>
    </v-card>

    <v-skeleton-loader
      v-if="loading && !categories.length"
      type="table"
    />

    <div
      v-else-if="!categories.length"
      class="text-body-2 grey--text py-6 text-center"
    >
      No product categories are linked to this audience's design groups, so there is nowhere to put a
      template yet.
    </div>

    <v-simple-table v-else>
      <template #default>
        <thead>
          <tr>
            <th class="text-left">
              Category
            </th>
            <th
              class="text-right"
              style="width: 110px"
            >
              Templates
            </th>
            <th
              class="text-left"
              style="width: 200px"
            >
              Coverage
            </th>
            <th
              class="text-right"
              style="width: 130px"
            >
              Groups at target
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="category in categories"
            :key="category.categoryId"
          >
            <td class="text-body-2">
              {{ category.categoryName }}
              <div
                v-if="category.blockedReason"
                class="text-caption warning--text"
              >
                {{ category.blockedReason }}
              </div>
            </td>
            <td
              class="text-right text-subtitle-2"
              :class="category.templateCount ? '' : 'grey--text'"
            >
              {{ category.templateCount }}
            </td>
            <td>
              <v-progress-linear
                :value="percent(category)"
                :color="coverageColor(category)"
                height="6"
                rounded
              />
              <div class="text-caption grey--text">
                {{ category.templateCount }} of {{ category.capacity }}
              </div>
            </td>
            <td class="text-right text-body-2">
              {{ category.groupsAtTarget }} / {{ category.designGroupCount }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import {
  audienceUrl,
  industryUrl,
  topicUrl,
  BRANDS_TAB
} from '@/helpers/templateDesigner';

export default {
  name: 'AudienceTemplates',
  props: {
    targetType: {
      type: Number,
      default: 0
    },
    targetId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      inventory: {}
    };
  },
  computed: {
    categories() {
      return this.inventory.categories || [];
    },
    // Gated on the audience actually having design groups: the Template Designer's industry and topic rails
    // are filtered to rows that have them, so a link for an audience without any lands on an empty stage.
    // designGroupCount is the same fact from this side, so it needs no extra round trip.
    designerUrl() {
      if (!this.inventory.designGroupCount) return null;
      return audienceUrl(this.targetType, this.targetId);
    },
    brandsUrl() {
      return audienceUrl(this.targetType, this.targetId, BRANDS_TAB);
    },
    brands() {
      return this.inventory.brands || {};
    },
    brandBuckets() {
      return this.brands.buckets || [];
    },
    // "na" is a real answer, not a missing one: an occasion or social-format audience has no business to
    // invent, so it is shown as settled rather than as a gap that can never be closed.
    brandChip() {
      const map = {
        na: { label: 'Not needed', color: 'grey', outlined: true },
        full: { label: 'Covered', color: 'success', outlined: false },
        partial: { label: 'Partial', color: 'warning', outlined: false },
        none: { label: 'None', color: 'error', outlined: false }
      };
      return map[this.brands.coverage] || map.none;
    },
    brandSummary() {
      const buckets = this.brandBuckets.length;
      if (!this.brands.needsBrand) {
        return (
          `${this.inventory.targetName || 'This audience'} isn't a business audience, so its templates ` +
          "aren't designed around a synthetic brand. Nothing to cover."
        );
      }

      return (
        `${this.brands.bucketsWithBrand} of ${buckets} bucket${buckets === 1 ? '' : 's'} ` +
        `${this.brands.bucketsWithBrand === 1 ? 'has' : 'have'} an approved brand — ` +
        `${this.brands.approvedCount} approved` +
        (this.brands.pendingCount ? `, ${this.brands.pendingCount} awaiting review` : '') +
        `, aiming for ${this.brands.minimum} per bucket. Every generated template is designed for one of these.`
      );
    },
    blockedGroupCount() {
      return this.brandBuckets
        .filter((b) => !b.hasBrand)
        .reduce((sum, b) => sum + b.designGroupCount, 0);
    }
  },
  mounted() {
    // The tab renders lazily, so mounting IS the first open.
    this.load();
  },
  methods: {
    load() {
      if (!this.targetId) return;

      this.loading = true;
      this.$store
        .dispatch('audiences/getTemplates', {
          targetType: this.targetType,
          targetId: this.targetId
        })
        .then((data) => {
          this.inventory = data || {};
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
    // Below the minimum is a warning, not a failure: one approved brand already unblocks generation, it just
    // means every template in that bucket is designed for the same fictional business.
    bucketColor(bucket) {
      if (!bucket.hasBrand) return 'error';
      if (this.brands.needsBrand && bucket.approved < this.brands.minimum) return 'warning';
      return 'success';
    },
    bucketTitle(bucket) {
      const groups = `${bucket.designGroupCount} design group${bucket.designGroupCount === 1 ? '' : 's'}`;
      const where = ' Opens the Brand Library for this bucket.';
      if (!bucket.hasBrand) return `No approved brand — ${groups} here can't be generated into.${where}`;
      return `${groups} in this bucket.${where}`;
    },
    // A bucket is (industry, topic); the topic-less one is the industry's own, so it opens the industry
    // workspace rather than a topic that doesn't exist.
    bucketUrl(bucket) {
      return bucket.topicId
        ? topicUrl(bucket.topicId, BRANDS_TAB)
        : industryUrl(bucket.industryId, BRANDS_TAB);
    },
    percent(category) {
      if (!category.capacity) return 0;
      return Math.min(100, (category.templateCount / category.capacity) * 100);
    },
    coverageColor(category) {
      const value = this.percent(category);
      if (value >= 100) return 'success';
      if (value >= 50) return 'warning';
      return 'error';
    }
  }
};
</script>
